import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Phone, X, Delete, Clock, User, Building2, PhoneCall, Plus, MessageSquare, RotateCcw, Send, PhoneOff, FileText, Mail, MapPin, Briefcase, Minus } from "lucide-react";
import TelnyxWebRTCCall from "./TelnyxWebRTCCall";

const testWebRTCCompatibility = () => {
  try {
    if (!window.RTCPeerConnection) return false;
    const pc = new RTCPeerConnection();
    if (typeof pc.getTransceivers !== 'function' || typeof pc.addTransceiver !== 'function') {
      pc.close(); return false;
    }
    try { pc.getTransceivers(); } catch (e) { pc.close(); return false; }
    pc.close();
    return true;
  } catch (e) {
    return false;
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhoneDialer({ isOpen, onClose }) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [recentNumbers, setRecentNumbers] = useState([]);
  const [sendingSms, setSendingSms] = useState(false);
  
  // Call status: 'idle' | 'calling' | 'connected' | 'ended'
  const [callStatus, setCallStatus] = useState("idle");
  const [callStartTime, setCallStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  
  // Mode: 'call' or 'sms'
  const [mode, setMode] = useState("call");
  const [smsMessage, setSmsMessage] = useState("");
  
  // Existing lead state
  const [existingLead, setExistingLead] = useState(null);
  const [existingLeadType, setExistingLeadType] = useState(null);
  
  // WebRTC state
  const [webrtcCallOpen, setWebrtcCallOpen] = useState(false);
  const [webrtcCallNumber, setWebrtcCallNumber] = useState(null);
  const [webrtcFailed, setWebrtcFailed] = useState(false);
  const [telnyxStatus, setTelnyxStatus] = useState({ configured: false, webrtc_enabled: false });
  const webrtcCompatibleRef = useRef(null);
  // Refs to avoid stale closures in useCallback handlers
  const existingLeadRef = useRef(null);
  const existingLeadTypeRef = useRef(null);

  // Log Call dialog
  const [showLogCallDialog, setShowLogCallDialog] = useState(false);
  const [callNotes, setCallNotes] = useState("");
  const [savingCallLog, setSavingCallLog] = useState(false);
  
  // Add to lead dialog (for new leads)
  const [showAddLeadDialog, setShowAddLeadDialog] = useState(false);
  const [dialedNumber, setDialedNumber] = useState("");
  const [selectedLogCallNumber, setSelectedLogCallNumber] = useState("");
  const [newLeadData, setNewLeadData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company_name: "",
    franchise_name: ""
  });
  const [savingLead, setSavingLead] = useState(false);

  // Minimize state for floating panel
  const [isMinimized, setIsMinimized] = useState(false);

  // Get all available phone numbers from the existing lead
  const availablePhoneNumbers = useMemo(() => {
    if (!existingLead) return [];
    
    const numbers = [];
    
    // CRM Lead fields
    if (existingLead.mobile_phone) {
      numbers.push({ label: 'Mobile', value: existingLead.mobile_phone });
    }
    if (existingLead.work_direct_phone) {
      numbers.push({ label: 'Work Direct', value: existingLead.work_direct_phone });
    }
    if (existingLead.work_hq_phone) {
      numbers.push({ label: 'Work HQ', value: existingLead.work_hq_phone });
    }
    
    // Franchise Lead fields
    if (existingLead.phone && !numbers.some(n => n.value === existingLead.phone)) {
      numbers.push({ label: 'Phone', value: existingLead.phone });
    }
    if (existingLead.mobile && !numbers.some(n => n.value === existingLead.mobile)) {
      numbers.push({ label: 'Mobile', value: existingLead.mobile });
    }
    
    return numbers;
  }, [existingLead]);

  // Set selected phone number when log call dialog opens
  useEffect(() => {
    if (showLogCallDialog && dialedNumber) {
      setSelectedLogCallNumber(dialedNumber);
    }
  }, [showLogCallDialog, dialedNumber]);

  // Fetch Telnyx status + check WebRTC compatibility once on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/telnyx/status`, { withCredentials: true });
        setTelnyxStatus(res.data);
      } catch {}
    };
    fetchStatus();
    if (webrtcCompatibleRef.current === null) {
      webrtcCompatibleRef.current = testWebRTCCompatibility();
    }
  }, []);

  // Keep refs in sync with state so useCallback handlers don't get stale closures
  useEffect(() => { existingLeadRef.current = existingLead; }, [existingLead]);
  useEffect(() => { existingLeadTypeRef.current = existingLeadType; }, [existingLeadType]);

  // Reset minimized state when dialer opens
  useEffect(() => {
    if (isOpen) {
      setIsMinimized(false);
    }
  }, [isOpen]);

  // Fetch recent dialed numbers
  useEffect(() => {
    if (isOpen) {
      fetchRecentNumbers();
    }
  }, [isOpen]);

  // Reset when dialog closes
  useEffect(() => {
    if (!isOpen) {
      handleReset();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [isOpen]);

  // Timer for elapsed time
  useEffect(() => {
    if (callStatus === "connected" && callStartTime) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [callStatus, callStartTime]);

  const fetchRecentNumbers = async () => {
    try {
      const response = await axios.get(`${API}/telnyx/recent-dialed?limit=3`, {
        withCredentials: true
      });
      setRecentNumbers(response.data.recent_numbers || []);
    } catch (error) {
      console.error("Error fetching recent numbers:", error);
    }
  };

  const lookupPhoneNumber = async (number) => {
    try {
      const response = await axios.get(`${API}/telnyx/lookup-phone/${encodeURIComponent(number)}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error("Error looking up phone:", error);
      return { found: false, lead: null, lead_type: null };
    }
  };

  const handleDigitPress = (digit) => {
    if (phoneNumber.length < 15 && callStatus === "idle") {
      setPhoneNumber(prev => prev + digit);
    }
  };

  const handleDelete = () => {
    if (callStatus === "idle") {
      setPhoneNumber(prev => prev.slice(0, -1));
    }
  };

  const handleReset = () => {
    setPhoneNumber("");
    setSmsMessage("");
    setMode("call");
    setCallStatus("idle");
    setElapsedTime(0);
    setCallStartTime(null);
    setExistingLead(null);
    setExistingLeadType(null);
    setCallNotes("");
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
  };

  const formatPhoneDisplay = (number) => {
    if (!number) return "";
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return number;
  };

  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCall = async (numberToCall = null) => {
    const number = numberToCall || phoneNumber;
    if (!number || number.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setCallStatus("calling");
    setDialedNumber(number);

    // Look up if this number exists in leads
    const lookupResult = await lookupPhoneNumber(number);
    if (lookupResult.found) {
      setExistingLead(lookupResult.lead);
      setExistingLeadType(lookupResult.lead_type);
      existingLeadRef.current = lookupResult.lead;
      existingLeadTypeRef.current = lookupResult.lead_type;
    } else {
      setExistingLead(null);
      setExistingLeadType(null);
      existingLeadRef.current = null;
      existingLeadTypeRef.current = null;
    }

    // Use WebRTC if Telnyx is configured and browser is compatible
    const useWebRTC = telnyxStatus.configured &&
                      telnyxStatus.webrtc_enabled &&
                      !webrtcFailed &&
                      webrtcCompatibleRef.current === true;

    if (useWebRTC) {
      setWebrtcCallNumber(number);
      setWebrtcCallOpen(true);
      setCallStatus("idle"); // Reset so PhoneDialer doesn't show its own in-call UI
      // Don't call onClose() — that unmounts the component and kills TelnyxWebRTCCall.
      // Instead, the panel hides itself when webrtcCallOpen is true (see showPanel above).
      fetchRecentNumbers();
      return;
    }

    // Fall back to server-side call
    try {
      const response = await axios.post(`${API}/telnyx/call`, {
        to_number: number,
        lead_id: lookupResult.found ? lookupResult.lead.id : null
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success(`Calling ${formatPhoneDisplay(number)}...`);

        // Simulate call connected after 3 seconds
        setTimeout(() => {
          setCallStatus("connected");
          setCallStartTime(Date.now());
        }, 3000);

        fetchRecentNumbers();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to initiate call";
      toast.error(errorMsg);
      setCallStatus("idle");
    }
  };

  // WebRTC callbacks — useCallback with [] deps so TelnyxWebRTCCall never gets stale refs
  const handleWebRTCCallConnected = useCallback(() => {
    // Don't set callStatus here — PhoneDialer panel is hidden during WebRTC calls.
    // The WebRTC Card widget handles its own UI state.
  }, []);

  const handleWebRTCCallEnded = useCallback((duration, recordingId) => {
    setElapsedTime(duration);
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    setCallStatus("idle");
    setTimeout(() => {
      if (existingLeadRef.current) {
        setShowLogCallDialog(true);
      } else {
        setShowAddLeadDialog(true);
      }
    }, 50);
  }, []);

  const handleWebRTCDurationUpdate = useCallback((duration) => {
    setElapsedTime(duration);
  }, []);

  const handleWebRTCError = useCallback((error, number) => {
    console.error('WebRTC error, falling back to server call:', error);
    setWebrtcFailed(true);
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    toast.info('Switching to phone call mode...');
    if (number) {
      axios.post(`${API}/telnyx/call`, {
        to_number: number,
        lead_id: existingLeadRef.current?.id || null
      }, { withCredentials: true })
        .then(res => {
          if (res.data.success) {
            toast.success(`Calling ${number}...`);
            setTimeout(() => {
              setCallStatus("connected");
              setCallStartTime(Date.now());
            }, 3000);
          }
        })
        .catch(err => {
          toast.error(err.response?.data?.detail || "Failed to initiate call");
          setCallStatus("idle");
        });
    }
  }, []);

  const handleEndCall = () => {
    setCallStatus("ended");
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Show appropriate dialog based on whether lead exists
    setTimeout(() => {
      if (existingLead) {
        setShowLogCallDialog(true);
      } else {
        setShowAddLeadDialog(true);
      }
      setCallStatus("idle");
    }, 100);
  };

  const handleSendSms = async (numberToSms = null) => {
    const number = numberToSms || phoneNumber;
    if (!number || number.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!smsMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingSms(true);
    
    // Look up if this number exists
    const lookupResult = await lookupPhoneNumber(number);
    
    try {
      const response = await axios.post(`${API}/telnyx/sms`, {
        to_number: number,
        message: smsMessage,
        lead_id: lookupResult.found ? lookupResult.lead.id : null
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success(`SMS sent to ${formatPhoneDisplay(number)}`);
        setDialedNumber(number);
        setSmsMessage("");
        
        if (lookupResult.found) {
          setExistingLead(lookupResult.lead);
          setExistingLeadType(lookupResult.lead_type);
        }
        
        // Show dialog based on whether lead exists
        setTimeout(() => {
          if (lookupResult.found) {
            toast.info(`Message logged to ${lookupResult.lead_type === 'crm' ? 'CRM' : 'Franchise'} lead: ${lookupResult.lead.first_name} ${lookupResult.lead.last_name}`);
          } else {
            setShowAddLeadDialog(true);
          }
        }, 1000);
        
        fetchRecentNumbers();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send SMS";
      toast.error(errorMsg);
    } finally {
      setSendingSms(false);
    }
  };

  const handleLogCall = async () => {
    if (!existingLead) return;
    
    // Use selected number or fall back to dialed number
    const phoneToLog = selectedLogCallNumber || dialedNumber;
    
    setSavingCallLog(true);
    try {
      await axios.post(`${API}/telnyx/log-call`, null, {
        params: {
          lead_id: existingLead.id,
          lead_type: existingLeadType,
          phone_number: phoneToLog,
          notes: callNotes,
          duration: elapsedTime,
          call_outcome: "completed"
        },
        withCredentials: true
      });
      
      toast.success("Call logged successfully!");
      setShowLogCallDialog(false);
      setCallNotes("");
      setElapsedTime(0);
      setSelectedLogCallNumber("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log call");
    } finally {
      setSavingCallLog(false);
    }
  };

  const handleAddLead = async (leadType) => {
    if (!newLeadData.email.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setSavingLead(true);
    try {
      const endpoint = leadType === "crm" 
        ? `${API}/crm/leads`
        : `${API}/franchise/leads`;

      const leadData = leadType === "crm" 
        ? {
            first_name: newLeadData.first_name,
            last_name: newLeadData.last_name,
            email: newLeadData.email,
            company_name: newLeadData.company_name,
            mobile_phone: dialedNumber
          }
        : {
            first_name: newLeadData.first_name,
            last_name: newLeadData.last_name,
            email: newLeadData.email,
            franchise_name: newLeadData.franchise_name,
            phone: dialedNumber
          };

      await axios.post(endpoint, leadData, { withCredentials: true });
      
      toast.success(`Lead added to ${leadType === "crm" ? "CRM" : "Franchise"} successfully!`);
      setShowAddLeadDialog(false);
      resetAddLeadForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add lead");
    } finally {
      setSavingLead(false);
    }
  };

  const resetAddLeadForm = () => {
    setNewLeadData({
      first_name: "",
      last_name: "",
      email: "",
      company_name: "",
      franchise_name: ""
    });
  };

  const handleSkipAddLead = () => {
    setShowAddLeadDialog(false);
    resetAddLeadForm();
  };

  const dialPadDigits = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["*", "0", "#"]
  ];

  const isValidNumber = phoneNumber.replace(/\D/g, "").length >= 10;
  const isInCall = callStatus === "calling" || callStatus === "connected";

  const getLeadDisplayName = (lead) => {
    if (!lead) return "Unknown";
    const name = `${lead.first_name || ""} ${lead.last_name || ""}`.trim();
    return name || lead.email || "Unknown";
  };

  const showPanel = isOpen && !webrtcCallOpen;

  return (
    <>
      {/* Minimized floating bubble */}
      {showPanel && isMinimized && createPortal(
        <button
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full pl-3 pr-4 py-3 shadow-lg transition-all duration-200 hover:scale-105 group"
          data-testid="dialer-minimized"
        >
          <div className="relative">
            <Phone className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full animate-ping" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full" />
          </div>
          {phoneNumber && (
            <span className="text-sm font-mono font-medium max-w-[120px] truncate">
              {formatPhoneDisplay(phoneNumber)}
            </span>
          )}
          {!phoneNumber && (
            <span className="text-sm font-medium">Dialer</span>
          )}
        </button>,
        document.body
      )}

      {/* Expanded floating panel */}
      {showPanel && !isMinimized && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200"
            onClick={() => {
              if (!isInCall) onClose();
            }}
          />
          {/* Centered card */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm" style={{ animation: 'dialerScaleIn 0.15s ease-out' }}>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                    <Phone className="h-5 w-5" />
                  </div>
                  <span className="font-bold text-sm">Phone Dialer</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                    title="Minimize"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (!isInCall) {
                        setIsMinimized(false);
                        onClose();
                      }
                    }}
                    className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Call Connected Screen */}
                {isInCall ? (
                  <div className="py-6 text-center space-y-4">
                    {/* Contact Info (if existing lead) */}
                    {existingLead && (
                      <div className="bg-slate-50 rounded-lg p-4 text-left mb-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            existingLeadType === 'crm' ? 'bg-blue-100' : 'bg-purple-100'
                          }`}>
                            <User className={`h-6 w-6 ${
                              existingLeadType === 'crm' ? 'text-blue-600' : 'text-purple-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">
                              {getLeadDisplayName(existingLead)}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              existingLeadType === 'crm'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {existingLeadType === 'crm' ? 'CRM Lead' : 'Franchise Lead'}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1 text-sm text-slate-600">
                          {existingLead.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5 text-slate-400" />
                              {existingLead.email}
                            </div>
                          )}
                          {(existingLead.company_name || existingLead.franchise_name) && (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3.5 w-3.5 text-slate-400" />
                              {existingLead.company_name || existingLead.franchise_name}
                            </div>
                          )}
                          {existingLead.title && (
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-3.5 w-3.5 text-slate-400" />
                              {existingLead.title}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                      callStatus === "calling" ? "bg-yellow-100" : "bg-emerald-100"
                    }`}>
                      <Phone className={`h-8 w-8 ${
                        callStatus === "calling" ? "text-yellow-600 animate-pulse" : "text-emerald-600"
                      }`} />
                    </div>

                    <div>
                      <p className={`text-lg font-semibold ${
                        callStatus === "calling" ? "text-yellow-700" : "text-emerald-700"
                      }`}>
                        {callStatus === "calling" ? "Calling..." : "Connected"}
                      </p>
                      <p className="text-xl font-mono font-bold text-slate-800 mt-1">
                        {formatPhoneDisplay(dialedNumber)}
                      </p>
                      {callStatus === "connected" && (
                        <p className="text-2xl font-mono text-emerald-600 mt-3">
                          {formatElapsedTime(elapsedTime)}
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={handleEndCall}
                      className="bg-red-600 hover:bg-red-700 h-12 px-8"
                      data-testid="end-call-button"
                    >
                      <PhoneOff className="mr-2 h-5 w-5" />
                      End Call
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Mode Toggle */}
                    <div className="flex rounded-lg bg-slate-100 p-1">
                      <button
                        onClick={() => setMode("call")}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                          mode === "call"
                            ? "bg-white text-emerald-700 shadow-sm"
                            : "text-slate-600 hover:text-slate-800"
                        }`}
                        data-testid="mode-call"
                      >
                        <PhoneCall className="h-4 w-4" />
                        Call
                      </button>
                      <button
                        onClick={() => setMode("sms")}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                          mode === "sms"
                            ? "bg-white text-blue-700 shadow-sm"
                            : "text-slate-600 hover:text-slate-800"
                        }`}
                        data-testid="mode-sms"
                      >
                        <MessageSquare className="h-4 w-4" />
                        SMS
                      </button>
                    </div>

                    {/* Phone number display */}
                    <div className="relative">
                      <Input
                        value={formatPhoneDisplay(phoneNumber)}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                        placeholder="Enter phone number"
                        className="text-center text-2xl font-mono h-14 pr-20"
                        data-testid="dialer-input"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                        {phoneNumber && (
                          <>
                            <button
                              onClick={handleReset}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Reset"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleDelete}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                              title="Delete"
                            >
                              <Delete className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* SMS Message Box */}
                    {mode === "sms" && (
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-600">Message</Label>
                        <Textarea
                          value={smsMessage}
                          onChange={(e) => setSmsMessage(e.target.value)}
                          placeholder="Type your SMS message here..."
                          className="min-h-[80px] resize-none"
                          maxLength={160}
                          data-testid="sms-message"
                        />
                        <p className="text-xs text-slate-400 text-right">
                          {smsMessage.length}/160 characters
                        </p>
                      </div>
                    )}

                    {/* Dial pad */}
                    <div className="grid grid-cols-3 gap-2">
                      {dialPadDigits.map((row, rowIndex) => (
                        row.map((digit, colIndex) => (
                          <Button
                            key={`${rowIndex}-${colIndex}`}
                            variant="outline"
                            className="h-12 text-xl font-semibold hover:bg-slate-100"
                            onClick={() => handleDigitPress(digit)}
                            data-testid={`dial-${digit}`}
                          >
                            {digit}
                          </Button>
                        ))
                      ))}
                    </div>

                    {/* Action button */}
                    <Button
                      className={`w-full h-12 ${
                        mode === "call"
                          ? "bg-emerald-600 hover:bg-emerald-700"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                      onClick={() => mode === "call" ? handleCall() : handleSendSms()}
                      disabled={sendingSms || !isValidNumber || (mode === "sms" && !smsMessage.trim())}
                      data-testid="action-button"
                    >
                      {sendingSms ? (
                        <span className="animate-pulse">Sending...</span>
                      ) : (
                        <>
                          {mode === "call" ? (
                            <>
                              <PhoneCall className="mr-2 h-5 w-5" />
                              Call
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-5 w-5" />
                              Send SMS
                            </>
                          )}
                        </>
                      )}
                    </Button>

                    {/* Recent numbers */}
                    {recentNumbers.length > 0 && (
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-600">Recent Calls</span>
                        </div>
                        <div className="space-y-1 max-h-36 overflow-y-auto">
                          {recentNumbers.map((item, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                setPhoneNumber(item.phone_number.replace(/\D/g, ""));
                              }}
                              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                              data-testid={`recent-${index}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                  {item.lead ? (
                                    <User className="h-4 w-4 text-slate-600" />
                                  ) : (
                                    <Phone className="h-4 w-4 text-slate-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-800">
                                    {item.lead
                                      ? `${item.lead.first_name || ""} ${item.lead.last_name || ""}`.trim() || "Unknown"
                                      : formatPhoneDisplay(item.phone_number)
                                    }
                                  </p>
                                  {item.lead && (
                                    <p className="text-xs text-slate-500">
                                      {item.lead.company_name || item.lead.franchise_name || formatPhoneDisplay(item.phone_number)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex items-center gap-2">
                                {item.lead_type && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    item.lead_type === "crm"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-purple-100 text-purple-700"
                                  }`}>
                                    {item.lead_type === "crm" ? "CRM" : "Franchise"}
                                  </span>
                                )}
                                <div className="flex gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMode("call");
                                      handleCall(item.phone_number);
                                    }}
                                    className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"
                                    title="Call"
                                  >
                                    <PhoneCall className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMode("sms");
                                      setPhoneNumber(item.phone_number.replace(/\D/g, ""));
                                    }}
                                    className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                                    title="SMS"
                                  >
                                    <MessageSquare className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Log Call Dialog (for existing leads) */}
      <Dialog open={showLogCallDialog} onOpenChange={setShowLogCallDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Log Call
            </DialogTitle>
            <DialogDescription>
              Add notes for this call with {getLeadDisplayName(existingLead)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Contact Details */}
            {existingLead && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    existingLeadType === 'crm' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    <User className={`h-6 w-6 ${
                      existingLeadType === 'crm' ? 'text-blue-600' : 'text-purple-600'
                    }`} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">
                      {getLeadDisplayName(existingLead)}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      existingLeadType === 'crm' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {existingLeadType === 'crm' ? 'CRM Lead' : 'Franchise Lead'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="h-4 w-4 text-slate-400" />
                    {formatPhoneDisplay(dialedNumber)}
                  </div>
                  {existingLead.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="h-4 w-4 text-slate-400" />
                      {existingLead.email}
                    </div>
                  )}
                  {(existingLead.company_name || existingLead.franchise_name) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      {existingLead.company_name || existingLead.franchise_name}
                    </div>
                  )}
                  {existingLead.title && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Briefcase className="h-4 w-4 text-slate-400" />
                      {existingLead.title}
                    </div>
                  )}
                  {(existingLead.city || existingLead.state) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {[existingLead.city, existingLead.state].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>

                {/* Phone Number Selection (if multiple numbers) */}
                {availablePhoneNumbers.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <Label className="text-xs text-slate-500 mb-2 block">Log call for number:</Label>
                    <Select
                      value={selectedLogCallNumber}
                      onValueChange={setSelectedLogCallNumber}
                    >
                      <SelectTrigger className="w-full" data-testid="log-call-phone-select">
                        <SelectValue placeholder="Select phone number" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePhoneNumbers.map((phone, idx) => (
                          <SelectItem key={idx} value={phone.value}>
                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-xs text-slate-500 w-20">{phone.label}:</span>
                              <span className="font-mono">{formatPhoneDisplay(phone.value)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {elapsedTime > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Call Duration: <span className="font-mono font-semibold text-slate-700">{formatElapsedTime(elapsedTime)}</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Call Notes */}
            <div className="space-y-2">
              <Label>Call Notes</Label>
              <Textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                placeholder="Enter notes about this call..."
                className="min-h-[100px] resize-none"
                data-testid="call-notes"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowLogCallDialog(false);
                setCallNotes("");
              }}
            >
              Skip
            </Button>
            <Button
              onClick={handleLogCall}
              disabled={savingCallLog}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-call-log-btn"
            >
              {savingCallLog ? "Saving..." : "Log Call"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WebRTC call component — renders a fixed Card widget via portal */}
      <TelnyxWebRTCCall
        isOpen={webrtcCallOpen}
        onClose={() => {
          setWebrtcCallOpen(false);
          setWebrtcCallNumber(null);
          setCallStatus("idle");
        }}
        phoneNumber={webrtcCallNumber || ""}
        leadId={existingLead?.id || null}
        leadName={getLeadDisplayName(existingLead)}
        companyName={existingLead?.company_name || existingLead?.franchise_name || ""}
        onCallConnected={handleWebRTCCallConnected}
        onCallEnded={handleWebRTCCallEnded}
        onDurationUpdate={handleWebRTCDurationUpdate}
        onError={handleWebRTCError}
      />

      {/* Add to Lead Dialog (for new leads) */}
      <Dialog open={showAddLeadDialog} onOpenChange={setShowAddLeadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Add to Lead?
            </DialogTitle>
            <DialogDescription>
              Add {formatPhoneDisplay(dialedNumber)} to CRM or Franchise
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">First Name</Label>
                  <Input
                    value={newLeadData.first_name}
                    onChange={(e) => setNewLeadData(prev => ({...prev, first_name: e.target.value}))}
                    placeholder="First name"
                    data-testid="lead-first-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Last Name</Label>
                  <Input
                    value={newLeadData.last_name}
                    onChange={(e) => setNewLeadData(prev => ({...prev, last_name: e.target.value}))}
                    placeholder="Last name"
                    data-testid="lead-last-name"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={newLeadData.email}
                  onChange={(e) => setNewLeadData(prev => ({...prev, email: e.target.value}))}
                  placeholder="email@example.com"
                  data-testid="lead-email"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Company / Franchise Name</Label>
                <Input
                  value={newLeadData.company_name || newLeadData.franchise_name}
                  onChange={(e) => setNewLeadData(prev => ({
                    ...prev, 
                    company_name: e.target.value,
                    franchise_name: e.target.value
                  }))}
                  placeholder="Company or Franchise name"
                  data-testid="lead-company"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Phone (auto-filled)</Label>
                <Input
                  value={formatPhoneDisplay(dialedNumber)}
                  disabled
                  className="bg-slate-100"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button 
              variant="outline" 
              onClick={handleSkipAddLead}
              className="w-full sm:w-auto"
            >
              Skip
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={() => handleAddLead("crm")}
                disabled={savingLead || !newLeadData.email.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="add-crm-btn"
              >
                {savingLead ? (
                  "Saving..."
                ) : (
                  <>
                    <Building2 className="mr-2 h-4 w-4" />
                    Add to CRM
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleAddLead("franchise")}
                disabled={savingLead || !newLeadData.email.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="add-franchise-btn"
              >
                {savingLead ? (
                  "Saving..."
                ) : (
                  <>
                    <User className="mr-2 h-4 w-4" />
                    Add to Franchise
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
